import { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CardMedia,
  Typography,
  Autocomplete,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileThumbnailsEstimates from "./FileThumbnailsEstimates";
import {
  pageContentCardSx,
  pageDropZoneSx,
  pageFieldSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";
import {
  applyIdentityToPresupuesto,
  getProductIdentityFields,
  getProductSupplyFields,
  findCatalogItem,
  hasAnyPresupuestoProductIdentity,
  hasPresupuestoProductIdentity,
} from "../../utils/newBudgetDrawerUtils";
import { fetchProductIdentity } from "../../api/Muestras/productosYMuestrasApi";

const BloqueProyectoEditar = ({
  newPresupuesto,
  setNewPresupuesto,
  proyectos,
  filteredProyectos,
  proveedores,
  temporadas,
  productosYMuestras,
  modelosTipo = [],
  inputValue,
  setInputValue,
  selectedCliente,
  setSelectedCliente,
  clientes,
  marcas,
  subgruposFromApi,
  incoterms,
  isEstimatesLocked,
  budget,
  localDesignClient,
  setLocalDesignClient,
  API_URL,
  axios,
  localNotas,
  setLocalNotas,
  handleSaveNotas,
  uploadedFiles,
  setUploadedFiles, // 🟢 nuevo
  uploadProjectFile, // 🟢 nuevo
  getFileContent, // 🟢 nuevo
  handleFileDelete,
  sampleImage,
  theme,
}) => {
  const [seasonDialogOpen, setSeasonDialogOpen] = useState(false);
  const [seasonDraft, setSeasonDraft] = useState("");
  const [savingSeason, setSavingSeason] = useState(false);

  // arriba del return, dentro de BloqueProyectoEditar
  const requiredState = {
    IdCliente: !!(selectedCliente ?? newPresupuesto?.IdCliente),
    IdMarca: !!newPresupuesto?.IdMarca,
    ProyectoId: !!newPresupuesto?.ProyectoId,
    IdProveedor: !!newPresupuesto?.IdProveedor,
    TemporadaId: !!newPresupuesto?.TemporadaId,
    designCliente: !!(newPresupuesto?.designCliente ?? "").trim(),
    IdSubgrupo: !!newPresupuesto?.IdSubgrupo,
    Id_Incoterm: !!newPresupuesto?.Id_Incoterm,
  };

  const labelByKey = {
    IdCliente: "Client",
    IdMarca: "Brand",
    ProyectoId: "Project",
    IdProveedor: "Supplier",
    TemporadaId: "Season",
    designCliente: "Client design name",
    IdSubgrupo: "Material",
    Id_Incoterm: "Incoterm",
  };

  const missingKeys = Object.entries(requiredState)
    .filter(([, ok]) => !ok)
    .map(([k]) => k);

  const editPanelSx = {
    borderRadius: 3,
    border: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.55),
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    p: { xs: 1.25, md: 1.5 },
  };

  const editHeroSx = {
    ...editPanelSx,
    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`,
    borderColor: alpha(theme.palette.primary.main, 0.12),
  };

  const readOnlyItemSx = {
    minWidth: 0,
    px: 1,
    py: 0.85,
    borderRadius: 2,
    bgcolor: alpha(theme.palette.background.default, 0.55),
  };

  const statPillSx = {
    flex: 1,
    minWidth: 0,
    px: 1,
    py: 0.85,
    borderRadius: 2,
    bgcolor: alpha(theme.palette.background.default, 0.55),
    textAlign: "center",
  };

  const fieldSx = pageFieldSx(theme);

  const seasonName =
    temporadas?.find(
      (temporada) =>
        String(temporada.ID_TEMPORADA) ===
        String(newPresupuesto.TemporadaId || newPresupuesto.Temporada),
    )?.TEMPORADA ||
    newPresupuesto?.TemporadaNombre ||
    "N/A";

  const openSeasonDialog = () => {
    if (isEstimatesLocked) return;
    setSeasonDraft(
      newPresupuesto.TemporadaId || newPresupuesto.Temporada || "",
    );
    setSeasonDialogOpen(true);
  };

  const handleSaveSeason = async () => {
    if (!seasonDraft && seasonDraft !== 0) return;
    const nextId = seasonDraft;

    setNewPresupuesto((prev) => ({
      ...prev,
      TemporadaId: nextId,
      Temporada: nextId,
    }));

    if (!newPresupuesto?.ProyectoId || !newPresupuesto?.IdPresupuesto) {
      setSeasonDialogOpen(false);
      return;
    }

    setSavingSeason(true);
    try {
      await axios.patch(
        `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
        { Temporada: nextId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setSeasonDialogOpen(false);
    } catch (error) {
      console.error("Error al guardar la temporada:", error);
      alert("Error al guardar la temporada.");
    } finally {
      setSavingSeason(false);
    }
  };

  const clientName =
    clientes?.find((c) => String(c.id) === String(selectedCliente))?.name ||
    "No Client Selected";

  const brandName =
    marcas?.find((m) => String(m.ID_MARCA) === String(newPresupuesto.IdMarca))
      ?.MARCA || "No Brand Selected";

  const statusMeta = {
    Created: {
      color: "#c896ff",
      label: "Created",
      date: newPresupuesto.FechaCrear || "No Date",
    },
    Offered: {
      color: "#34afc7",
      label: "Offered",
      date: newPresupuesto.FechaPresentacion || "No Date",
    },
    Cancelled: {
      color: "#f44336",
      label: "Cancelled",
      date: newPresupuesto.FechaCancelacion || "No Date",
    },
    Accepted: {
      color: "#00c2a3",
      label: "Accepted",
      date: newPresupuesto.FechaAceptacion || "No Date",
    },
  };

  const activeStatus = statusMeta[newPresupuesto.Estado] || {
    color: theme.palette.text.secondary,
    label: newPresupuesto.Estado || "Status",
    date: "No Date",
  };

  const handleFiles = async (files) => {
    for (const file of files) {
      if (!file) continue;
      if (!newPresupuesto?.IdPresupuesto) {
        alert("Crea primero el presupuesto antes de subir archivos.");
        return;
      }

      // Preview instantáneo
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setUploadedFiles((prev) => [
            ...prev,
            {
              fileName: file.name,
              mimeType: file.type,
              data: event.target.result.split(",")[1],
            },
          ]);
        };
        reader.readAsDataURL(file);
      }

      try {
        const res = await uploadProjectFile(file, newPresupuesto.IdPresupuesto);
        console.log("✅ Archivo subido:", res.filePath);

        if (budget?.NumeroPresupuesto) {
          const updatedFiles = await getFileContent(budget.NumeroPresupuesto);
          if (updatedFiles.length > 0) {
            setUploadedFiles(updatedFiles);
          }
        }
      } catch (err) {
        console.error("❌ Error al subir archivo:", err);
      }
    }
  };

  useEffect(() => {
    const designName = newPresupuesto?.DiseñoMuestra;
    if (!designName) return;

    const needsIdentity = !hasPresupuestoProductIdentity(newPresupuesto);
    const needsSupply =
      !newPresupuesto?.TemporadaId ||
      !newPresupuesto?.IdSubgrupo ||
      !newPresupuesto?.IdProveedor;

    if (!needsIdentity && !needsSupply) return;

    const item = productosYMuestras?.length
      ? findCatalogItem(productosYMuestras, {
          idProducto: newPresupuesto?.IdProducto,
          idMuestra: newPresupuesto?.IdMuestra,
          nombre: designName,
        })
      : null;

    if (item) {
      const fromCatalog = getProductIdentityFields(item, modelosTipo);
      const supply = getProductSupplyFields(item, proveedores);
      const hasIdentity = hasAnyPresupuestoProductIdentity(fromCatalog);
      const hasSupply = Object.keys(supply).length > 0;

      if (hasIdentity || hasSupply) {
        const alreadySynced =
          String(newPresupuesto?.TipoPrenda ?? "") ===
            String(fromCatalog.TipoPrenda ?? "") &&
          String(newPresupuesto?.ABREVIATURA ?? "") ===
            String(fromCatalog.ABREVIATURA ?? "") &&
          String(newPresupuesto?.VOL ?? "") === String(fromCatalog.VOL ?? "") &&
          (!supply.TemporadaId ||
            String(newPresupuesto?.TemporadaId) ===
              String(supply.TemporadaId)) &&
          (!supply.IdSubgrupo ||
            String(newPresupuesto?.IdSubgrupo) === String(supply.IdSubgrupo)) &&
          (!supply.IdProveedor ||
            String(newPresupuesto?.IdProveedor) === String(supply.IdProveedor));
        if (alreadySynced) return;

        setNewPresupuesto((prev) => {
          const next = { ...prev };
          if (needsIdentity && hasIdentity) {
            next.TipoPrenda = fromCatalog.TipoPrenda || prev.TipoPrenda || null;
            next.ABREVIATURA =
              fromCatalog.ABREVIATURA || prev.ABREVIATURA || null;
            next.VOL =
              prev.VOL !== null &&
              prev.VOL !== undefined &&
              String(prev.VOL).trim() !== ""
                ? prev.VOL
                : (fromCatalog.VOL ?? null);
            next.IdProducto = prev.IdProducto ?? fromCatalog.IdProducto ?? null;
            next.IdMuestra = prev.IdMuestra ?? fromCatalog.IdMuestra ?? null;
          }
          if (needsSupply && hasSupply) {
            if (!prev.TemporadaId && supply.TemporadaId != null) {
              next.TemporadaId = supply.TemporadaId;
            }
            if (!prev.IdSubgrupo && supply.IdSubgrupo != null) {
              next.IdSubgrupo = supply.IdSubgrupo;
            }
            if (!prev.IdProveedor && supply.IdProveedor != null) {
              next.IdProveedor = supply.IdProveedor;
            }
          }
          return next;
        });
        if (!needsIdentity || hasIdentity) return;
      }
    }

    if (!needsIdentity) return;

    let cancelled = false;
    (async () => {
      try {
        const fromApi = await fetchProductIdentity({
          nombre: designName,
          idProducto: newPresupuesto?.IdProducto,
          idMuestra: newPresupuesto?.IdMuestra,
        });
        if (cancelled || !hasAnyPresupuestoProductIdentity(fromApi)) return;

        setNewPresupuesto((prev) => applyIdentityToPresupuesto(prev, fromApi));
      } catch (error) {
        console.error("Error resolviendo identity desde API:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    productosYMuestras,
    modelosTipo,
    proveedores,
    newPresupuesto?.DiseñoMuestra,
    newPresupuesto?.IdProducto,
    newPresupuesto?.IdMuestra,
    newPresupuesto?.TipoPrenda,
    newPresupuesto?.ABREVIATURA,
    newPresupuesto?.VOL,
    newPresupuesto?.TemporadaId,
    newPresupuesto?.IdSubgrupo,
    newPresupuesto?.IdProveedor,
    setNewPresupuesto,
  ]);

  const createSectionSx = {
    ...pageContentCardSx(theme),
    p: { xs: 1.5, sm: 1.75 },
  };

  const createFieldsGridSx = (cols = 2) => ({
    display: "grid",
    gridTemplateColumns: cols === 1 ? "1fr" : { xs: "1fr", sm: "1fr 1fr" },
    gap: 1.25,
    mt: 1.1,
  });

  return !newPresupuesto.IdPresupuesto ? (
    <Box
      sx={{
        width: "100%",
        maxWidth: 820,
        mx: "auto",
        pb: 1,
      }}
    >
      <Stack spacing={1.5}>
        <Box
          sx={{
            ...createSectionSx,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1.25,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...pageSectionTitleSx, mb: 0.35 }}>
              Progress
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: 1.3 }}
            >
              {missingKeys.length === 0
                ? "Ready to create"
                : `${Object.keys(requiredState).length - missingKeys.length}/${Object.keys(requiredState).length} required fields`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {Object.keys(requiredState).map((key) => {
              const ok = requiredState[key];
              return (
                <Chip
                  key={key}
                  size="small"
                  label={labelByKey[key]}
                  color={ok ? "success" : "default"}
                  variant={ok ? "filled" : "outlined"}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    opacity: ok ? 1 : 0.75,
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        <Box sx={createSectionSx}>
          <Typography sx={pageSectionTitleSx}>Client & project</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
          >
            Pick the client first — brand and project unlock from there.
          </Typography>
          <Box sx={createFieldsGridSx(2)}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel required>Client</InputLabel>
              <Select
                label="Client"
                value={selectedCliente}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedCliente(value);
                  setNewPresupuesto((prev) => {
                    const currentProject = proyectos.find(
                      (p) => String(p.id) === String(prev.ProyectoId),
                    );
                    const projectBelongs =
                      currentProject &&
                      String(currentProject.customerId) === String(value);

                    return {
                      ...prev,
                      IdCliente: value,
                      IdMarca: "",
                      ...(projectBelongs
                        ? {}
                        : { ProyectoId: "", TemporadaId: "" }),
                    };
                  });
                }}
                disabled={!!newPresupuesto.IdPresupuesto}
              >
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={fieldSx}
              disabled={!selectedCliente}
            >
              <InputLabel required>Brand</InputLabel>
              <Select
                label="Brand"
                name="IdMarca"
                value={newPresupuesto.IdMarca}
                onChange={(e) =>
                  setNewPresupuesto({
                    ...newPresupuesto,
                    IdMarca: e.target.value,
                  })
                }
                disabled={!!newPresupuesto.IdPresupuesto}
              >
                {marcas.map((marca) => (
                  <MenuItem key={marca.ID_MARCA} value={marca.ID_MARCA}>
                    {marca.MARCA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={{ ...fieldSx, gridColumn: { xs: "auto", sm: "1 / -1" } }}
              disabled={!selectedCliente || !!newPresupuesto.IdPresupuesto}
            >
              <InputLabel required>Project</InputLabel>
              <Select
                label="Project"
                name="ProyectoId"
                value={newPresupuesto.ProyectoId}
                onChange={(e) => {
                  const selectedProyectoId = e.target.value;
                  const selectedProject = proyectos.find(
                    (proyecto) =>
                      String(proyecto.id) === String(selectedProyectoId),
                  );

                  if (selectedProject) {
                    const seasonFound = temporadas.find(
                      (temporada) =>
                        temporada.TEMPORADA.trim().toLowerCase() ===
                        selectedProject.proyectType.trim().toLowerCase(),
                    );

                    setNewPresupuesto((prev) => ({
                      ...prev,
                      ProyectoId: selectedProyectoId,
                      TemporadaId:
                        prev.TemporadaId ||
                        (seasonFound ? seasonFound.ID_TEMPORADA : ""),
                      IdCliente: selectedProject.customerId || prev.IdCliente,
                      IdMarca: selectedProject.marcaId || prev.IdMarca || "",
                    }));

                    if (selectedProject.customerId) {
                      setSelectedCliente(selectedProject.customerId);
                    }
                  }
                }}
              >
                {filteredProyectos.map((proyecto) => (
                  <MenuItem key={proyecto.id} value={proyecto.id}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        py: 0.25,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 800, fontSize: "0.875rem" }}
                      >
                        {proyecto.subject}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {proyecto.notes || "No notes available"}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={createSectionSx}>
          <Typography sx={pageSectionTitleSx}>Supply</Typography>
          <Box sx={createFieldsGridSx(2)}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel required>Supplier</InputLabel>
              <Select
                label="Supplier"
                name="IdProveedor"
                value={newPresupuesto.IdProveedor || ""}
                onChange={(e) =>
                  setNewPresupuesto({
                    ...newPresupuesto,
                    IdProveedor: e.target.value,
                  })
                }
                disabled={!!newPresupuesto.IdPresupuesto}
              >
                {proveedores.map((proveedor) => (
                  <MenuItem key={proveedor.CODPROV} value={proveedor.CODPROV}>
                    {proveedor.N_PROVEEDOR}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel required>Season</InputLabel>
              <Select
                label="Season"
                name="TemporadaId"
                value={newPresupuesto.TemporadaId || ""}
                onChange={(e) =>
                  setNewPresupuesto({
                    ...newPresupuesto,
                    TemporadaId: e.target.value,
                  })
                }
                disabled={!!newPresupuesto.IdPresupuesto}
              >
                {temporadas.map((temporada) => (
                  <MenuItem
                    key={temporada.ID_TEMPORADA}
                    value={temporada.ID_TEMPORADA}
                  >
                    {temporada.TEMPORADA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={createSectionSx}>
          <Typography sx={pageSectionTitleSx}>Product</Typography>
          <Box sx={createFieldsGridSx(1)}>
            <Autocomplete
              fullWidth
              size="small"
              options={productosYMuestras
                .filter((item) =>
                  item.Nombre.toLowerCase().includes(inputValue.toLowerCase()),
                )
                .slice(0, 15)}
              getOptionLabel={(option) => option.Nombre || ""}
              value={
                productosYMuestras.find(
                  (item) => item.Nombre === newPresupuesto.DiseñoMuestra,
                ) || null
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  const identity = getProductIdentityFields(
                    newValue,
                    modelosTipo,
                  );
                  const supply = getProductSupplyFields(newValue, proveedores);
                  setNewPresupuesto((prev) => ({
                    ...prev,
                    DiseñoMuestra: newValue.Nombre,
                    designCliente: newValue.Nombre,
                    ...identity,
                    ...supply,
                  }));

                  if (!hasPresupuestoProductIdentity(identity)) {
                    fetchProductIdentity({
                      nombre: newValue.Nombre,
                      idProducto: identity.IdProducto,
                      idMuestra: identity.IdMuestra,
                    })
                      .then((fromApi) => {
                        if (!hasAnyPresupuestoProductIdentity(fromApi)) return;
                        setNewPresupuesto((prev) =>
                          applyIdentityToPresupuesto(prev, fromApi),
                        );
                      })
                      .catch((error) => {
                        console.error(
                          "Error resolviendo identity al seleccionar:",
                          error,
                        );
                      });
                  }
                } else {
                  setNewPresupuesto((prev) => ({
                    ...prev,
                    DiseñoMuestra: "",
                    designCliente: "",
                    IdProducto: null,
                    IdMuestra: null,
                    TipoPrenda: "",
                    ABREVIATURA: "",
                    VOL: "",
                    IdSubgrupo: "",
                  }));
                }
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) =>
                setInputValue(newInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Sample / Design"
                  sx={fieldSx}
                  disabled={!!newPresupuesto.IdPresupuesto}
                />
              )}
            />

            {(newPresupuesto?.DiseñoMuestra ||
              newPresupuesto?.TipoPrenda ||
              newPresupuesto?.ABREVIATURA ||
              (newPresupuesto?.VOL !== null &&
                newPresupuesto?.VOL !== undefined &&
                String(newPresupuesto?.VOL).trim() !== "")) && (
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.25 }}>
                {[
                  { label: "Type", value: newPresupuesto?.TipoPrenda || "—" },
                  { label: "Code", value: newPresupuesto?.ABREVIATURA || "—" },
                  {
                    label: "Volume",
                    value:
                      newPresupuesto?.VOL !== null &&
                      newPresupuesto?.VOL !== undefined &&
                      String(newPresupuesto?.VOL).trim() !== ""
                        ? `${newPresupuesto.VOL} m³`
                        : "— m³",
                  },
                ].map((item) => (
                  <Box key={item.label} sx={statPillSx}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, display: "block", mb: 0.2 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Client design name"
                required
                value={newPresupuesto?.designCliente || ""}
                onChange={(e) =>
                  setNewPresupuesto((prev) => ({
                    ...prev,
                    designCliente: e.target.value,
                  }))
                }
                sx={fieldSx}
                disabled={isEstimatesLocked || !selectedCliente}
                inputProps={{ maxLength: 500 }}
              />

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel required>Material</InputLabel>
                <Select
                  label="Material"
                  value={newPresupuesto.IdSubgrupo || ""}
                  onChange={(e) =>
                    setNewPresupuesto((prev) => ({
                      ...prev,
                      IdSubgrupo: e.target.value,
                    }))
                  }
                  disabled={isEstimatesLocked}
                >
                  {subgruposFromApi.map((item) => (
                    <MenuItem key={item.ID_SUBGRUPO} value={item.ID_SUBGRUPO}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        gap={1}
                      >
                        <span>{item.SUBGRUPO}</span>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          🇨🇳 {item.DutiesChina ?? 0}% / 🇮🇳{" "}
                          {item.DutiesIndia ?? 0}%
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        <Box sx={createSectionSx}>
          <Typography sx={pageSectionTitleSx}>Commercial</Typography>
          <Box sx={createFieldsGridSx(2)}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel required>Incoterm</InputLabel>
              <Select
                label="Incoterm"
                value={newPresupuesto.Id_Incoterm || ""}
                onChange={(e) =>
                  setNewPresupuesto((prev) => ({
                    ...prev,
                    Id_Incoterm: e.target.value,
                  }))
                }
                disabled={isEstimatesLocked}
              >
                {incoterms.map((item) => (
                  <MenuItem key={item.Id_Incoterm} value={item.Id_Incoterm}>
                    {item.Incoterm}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Target €"
              value={newPresupuesto.CustomerTarget || ""}
              onChange={(e) =>
                setNewPresupuesto({
                  ...newPresupuesto,
                  CustomerTarget: e.target.value,
                })
              }
              sx={fieldSx}
              disabled={isEstimatesLocked}
              type="number"
            />

            <TextField
              fullWidth
              size="small"
              label="PVP €"
              value={newPresupuesto.PVP || ""}
              onChange={(e) =>
                setNewPresupuesto({
                  ...newPresupuesto,
                  PVP: e.target.value,
                })
              }
              sx={fieldSx}
              disabled={isEstimatesLocked}
              type="number"
            />

            <TextField
              fullWidth
              size="small"
              label="MarkUp"
              value={newPresupuesto.MarkUp || ""}
              disabled
              sx={fieldSx}
              type="number"
            />

            <TextField
              fullWidth
              size="small"
              label="Notes"
              multiline
              minRows={2}
              value={newPresupuesto?.Notas || ""}
              onChange={(e) =>
                setNewPresupuesto((prev) => ({
                  ...prev,
                  Notas: e.target.value,
                }))
              }
              sx={{ ...fieldSx, gridColumn: { xs: "auto", sm: "1 / -1" } }}
              disabled={isEstimatesLocked || !selectedCliente}
              inputProps={{ maxLength: 500 }}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  ) : (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Box sx={{ ...editHeroSx, border: "1px solid #0000008c !important" }}>
        <Typography sx={pageSectionTitleSx}>Sample / design</Typography>
        <Typography
          sx={{
            mt: 0.35,
            fontWeight: 800,
            fontSize: { xs: "1.15rem", md: "1.3rem" },
            lineHeight: 1.2,
            color: "primary.dark",
            letterSpacing: -0.2,
          }}
        >
          {newPresupuesto.DiseñoMuestra || "No design selected"}
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Client design name"
          value={localDesignClient}
          onChange={(e) => setLocalDesignClient(e.target.value)}
          onBlur={async () => {
            if (!newPresupuesto?.ProyectoId || !newPresupuesto?.IdPresupuesto)
              return;

            const next = (localDesignClient ?? "").trim();
            const payload = { designCliente: next === "" ? null : next };
            const auth = {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            };

            try {
              const urlPatch = `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`;
              await axios.patch(urlPatch, payload, auth);
              setNewPresupuesto((prev) => ({
                ...prev,
                designCliente: payload.designCliente,
              }));
            } catch (errPatch) {
              console.error("PATCH designCliente falló:", {
                status: errPatch?.response?.status,
                data: errPatch?.response?.data,
                message: errPatch?.message,
              });
              try {
                const urlPut = `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`;
                await axios.put(urlPut, payload, auth);
                setNewPresupuesto((prev) => ({
                  ...prev,
                  designCliente: payload.designCliente,
                }));
              } catch (errPut) {
                console.error("PUT designCliente también falló:", {
                  status: errPut?.response?.status,
                  data: errPut?.response?.data,
                  message: errPut?.message,
                });
                alert("Error al guardar el nombre de producto del cliente.");
              }
            }
          }}
          sx={{ ...fieldSx, mt: 1.25 }}
          disabled={isEstimatesLocked}
          inputProps={{ maxLength: 500 }}
        />

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.1, rowGap: 0.5 }}
        >
          <Chip
            size="small"
            label={activeStatus.label}
            sx={{
              fontWeight: 800,
              bgcolor: alpha(activeStatus.color, 0.12),
              color: activeStatus.color,
              border: "1px solid",
              borderColor: alpha(activeStatus.color, 0.22),
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            {activeStatus.date}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mt: 1.1 }}>
          {[
            { label: "Type", value: newPresupuesto?.TipoPrenda || "—" },
            { label: "Code", value: newPresupuesto?.ABREVIATURA || "—" },
            {
              label: "Volume",
              value: `${newPresupuesto?.VOL ?? "—"} m³`,
            },
          ].map((item) => (
            <Box key={item.label} sx={statPillSx}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, display: "block", mb: 0.2 }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box sx={editPanelSx}>
        <Typography sx={pageSectionTitleSx}>Product setup</Typography>
        <Stack spacing={1.1} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Material</InputLabel>
            <Select
              label="Material"
              value={newPresupuesto.IdSubgrupo || ""}
              onChange={(e) =>
                setNewPresupuesto((prev) => ({
                  ...prev,
                  IdSubgrupo: e.target.value,
                }))
              }
              disabled={isEstimatesLocked}
            >
              {subgruposFromApi.map((item) => (
                <MenuItem key={item.ID_SUBGRUPO} value={item.ID_SUBGRUPO}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <span>{item.SUBGRUPO}</span>
                    <span style={{ fontSize: "0.8em", color: "#666" }}>
                      🇨🇳 {item.DutiesChina ?? 0}% / 🇮🇳 {item.DutiesIndia ?? 0}%
                    </span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Supplier</InputLabel>
            <Select
              label="Supplier"
              name="IdProveedor"
              value={newPresupuesto.IdProveedor || ""}
              onChange={(e) => {
                setNewPresupuesto((prev) => ({
                  ...prev,
                  IdProveedor: e.target.value,
                }));
              }}
              onBlur={async () => {
                if (!newPresupuesto.IdPresupuesto) return;
                try {
                  await axios.patch(
                    `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
                    { IdProveedor: newPresupuesto.IdProveedor },
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                      },
                    },
                  );
                } catch (error) {
                  console.error("Error al guardar el proveedor:", error);
                  alert("Error al guardar el proveedor.");
                }
              }}
              disabled={isEstimatesLocked}
            >
              {proveedores.map((proveedor) => (
                <MenuItem key={proveedor.CODPROV} value={proveedor.CODPROV}>
                  {proveedor.N_PROVEEDOR}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Box sx={editPanelSx}>
        <Typography sx={pageSectionTitleSx}>Business context</Typography>

        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              ...readOnlyItemSx,
              cursor: isEstimatesLocked ? "default" : "pointer",
              "&:hover": isEstimatesLocked
                ? undefined
                : {
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
            }}
            onClick={openSeasonDialog}
            role={isEstimatesLocked ? undefined : "button"}
            title={isEstimatesLocked ? undefined : "Change season"}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5,
                mb: 0.25,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Season
              </Typography>
              {!isEstimatesLocked && (
                <EditOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary", opacity: 0.7 }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: 1.25 }}
              noWrap
              title={seasonName}
            >
              {seasonName}
            </Typography>
          </Box>

          {[
            { label: "Client", value: clientName },
            { label: "Brand", value: brandName },
          ].map((item) => (
            <Box key={item.label} sx={readOnlyItemSx}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: "block", mb: 0.25 }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, lineHeight: 1.25 }}
                noWrap
                title={item.value}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Dialog
          open={seasonDialogOpen}
          onClose={savingSeason ? undefined : () => setSeasonDialogOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 800, pr: 6, position: "relative" }}>
            Change season
            <IconButton
              aria-label="close"
              onClick={() => setSeasonDialogOpen(false)}
              disabled={savingSeason}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth size="small" sx={{ mt: 1, ...fieldSx }}>
              <InputLabel>Season</InputLabel>
              <Select
                label="Season"
                value={seasonDraft || ""}
                onChange={(e) => setSeasonDraft(e.target.value)}
                disabled={savingSeason}
              >
                {temporadas.map((temporada) => (
                  <MenuItem
                    key={temporada.ID_TEMPORADA}
                    value={temporada.ID_TEMPORADA}
                  >
                    {temporada.TEMPORADA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setSeasonDialogOpen(false)}
              disabled={savingSeason}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveSeason}
              disabled={savingSeason || !seasonDraft}
            >
              {savingSeason ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Stack spacing={1.1} sx={{ mt: 1.1 }}>
          <TextField
            fullWidth
            size="small"
            label="Notes"
            multiline
            minRows={2}
            value={localNotas}
            onChange={(e) => setLocalNotas(e.target.value)}
            onBlur={handleSaveNotas}
            sx={fieldSx}
          />

          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Incoterm</InputLabel>
            <Select
              label="Incoterm"
              value={newPresupuesto.Id_Incoterm || ""}
              onChange={async (e) => {
                if (
                  !newPresupuesto?.ProyectoId ||
                  !newPresupuesto?.IdPresupuesto
                ) {
                  setNewPresupuesto((prev) => ({
                    ...prev,
                    Id_Incoterm: e.target.value,
                  }));
                  return;
                }
                const nextId = e.target.value;
                setNewPresupuesto((prev) => ({ ...prev, Id_Incoterm: nextId }));
                try {
                  await axios.patch(
                    `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
                    { Id_Incoterm: nextId },
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                      },
                    },
                  );
                } catch (err) {
                  const isCanceled =
                    (axios?.isCancel && axios.isCancel(err)) ||
                    err?.code === "ERR_CANCELED" ||
                    (err?.message || "").toLowerCase().includes("canceled");
                  if (!isCanceled) {
                    console.warn("Incoterm no guardado (silenciado):", {
                      status: err?.response?.status,
                      data: err?.response?.data,
                      message: err?.message,
                    });
                  }
                }
              }}
              disabled={isEstimatesLocked}
            >
              {incoterms.map((item) => (
                <MenuItem key={item.Id_Incoterm} value={item.Id_Incoterm}>
                  {item.Incoterm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 0.75,
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Target €"
              value={newPresupuesto.CustomerTarget || ""}
              onChange={(e) => {
                const value = e.target.value;
                setNewPresupuesto((prev) => ({
                  ...prev,
                  CustomerTarget: value ? parseFloat(value) || "" : null,
                }));
              }}
              sx={{
                ...fieldSx,
                "& input[type=number]": { MozAppearance: "textfield" },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                  { WebkitAppearance: "none", margin: 0 },
              }}
              type="number"
            />
            <TextField
              fullWidth
              size="small"
              label="PVP €"
              value={newPresupuesto.PVP || ""}
              onChange={(e) => {
                const value = e.target.value;
                setNewPresupuesto((prev) => ({
                  ...prev,
                  PVP: value ? parseFloat(value) || "" : null,
                }));
              }}
              sx={{
                ...fieldSx,
                "& input[type=number]": { MozAppearance: "textfield" },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                  { WebkitAppearance: "none", margin: 0 },
              }}
              type="number"
            />
            <TextField
              fullWidth
              size="small"
              label="MarkUp"
              value={newPresupuesto.MarkUp || ""}
              disabled
              sx={fieldSx}
              type="number"
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={editPanelSx}>
        <Typography sx={pageSectionTitleSx}>Reference assets</Typography>
        <Box
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const files = Array.from(e.dataTransfer.files);
            await handleFiles(files);
          }}
          onPaste={async (e) => {
            const items = Array.from(e.clipboardData.items);
            const files = items
              .filter((item) => item.kind === "file")
              .map((item) => item.getAsFile())
              .filter(Boolean);
            if (files.length) {
              e.preventDefault();
              await handleFiles(files);
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
          sx={{
            ...pageDropZoneSx(theme),
            mt: 1,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            Drag, paste, or upload reference files
          </Typography>

          <Button
            component="label"
            variant="outlined"
            size="small"
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Choose files
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp, application/pdf"
              hidden
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                await handleFiles(files);
                e.target.value = "";
              }}
            />
          </Button>

          {uploadedFiles.length > 0 ? (
            <Stack spacing={1} sx={{ mt: 1.15, textAlign: "left" }}>
              {uploadedFiles.map((file, index) => (
                <FileThumbnailsEstimates
                  key={index}
                  filePath={file}
                  onDelete={handleFileDelete}
                />
              ))}
            </Stack>
          ) : (
            <CardMedia
              component="img"
              height="180"
              image={sampleImage}
              alt="Default sample"
              sx={{
                mt: 1,
                opacity: 0.7,
                borderRadius: 2,
                objectFit: "contain",
                bgcolor: alpha(theme.palette.common.black, 0.02),
              }}
            />
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default BloqueProyectoEditar;
